import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { parseDateToLocal } from '../../../../../../shared/utils';
import { surveyRowId } from '../../../../../../shared/surveys';
import CustomerCareDTable from '../../../../../Shared/CustomerCareDTable';
import NoDataCard from '../../../../../Dashboard/detailPanel/NoDataCard';
import SurveyResponseChip from '../SurveyResponseChip';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomerWellnessResponses } from '../../../../../../store/customers/actionCreators';
import LoadingSpinner from '../../../../../Shared/LoadingSpinner';
import type {
  CachedCustomerSurveyResponses,
  CustomerSurveyResponse,
} from '../../../../../../store/customers/customerTypes';

// Daily wellness survey (DWS) history. Same endpoint as CSATDetailsPanel, but
// requests survey_type_id=2 rows (1-5 scale) into their own store slot.
const DWSDetailsPanel = ({ customerId }: { customerId: number }) => {
  const dispatch = useDispatch();
  const { customerWellnessResponses, customerWellnessLoading } = useSelector(
    (state: any) => state.customers,
  ) as {
    customerWellnessResponses: CachedCustomerSurveyResponses | undefined;
    customerWellnessLoading: boolean;
  };

  useEffect(() => {
    // Only fetch if not already loaded for this customer
    if (
      !customerWellnessResponses ||
      (customerWellnessResponses as { customerId?: number }).customerId !==
        customerId
    ) {
      dispatch(fetchCustomerWellnessResponses(customerId));
    }
  }, [dispatch, customerId]);

  const columns = [
    {
      field: 'response',
      headerName: 'Survey response',
      flex: 2,
      renderCell: (params: any) => (
        <SurveyResponseChip response={params.row.response} isCurrent />
      ),
    },
    {
      field: 'received_at',
      headerName: 'Response time',
      flex: 2,
      renderCell: (params: any) => (
        <span>
          {params.row.received_at
            ? parseDateToLocal(params.row.received_at, 'yyyy-MM-dd HH:mm:ss')
            : 'N/A'}
        </span>
      ),
    },
  ];

  // Same cached shape as the Issues panel: an array, or the array spread into
  // an object plus `customerId`. Survey rows have no DB id, so derive one.
  const surveyRows: CustomerSurveyResponse[] = Array.isArray(
    customerWellnessResponses,
  )
    ? customerWellnessResponses
    : (Object.values(customerWellnessResponses || {}).filter(
        (item: any) => typeof item === 'object' && item.score !== undefined,
      ) as CustomerSurveyResponse[]);
  const rows = surveyRows.map((row) => ({ ...row, id: surveyRowId(row) }));

  if (customerWellnessLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={customerWellnessLoading}
            pageSize={4}
            defaultSortField="received_at"
            defaultSortDirection="desc"
            hideViewAllButton
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default DWSDetailsPanel;
